/** Central brand + simulation constants. Change branding here only. */
export const BRAND = { name: 'PAPERXIOM', short: 'PX', tagline: 'Memecoin trading simulator and reaction trainer (simulated, no real trades)' } as const;
/** The project's real token + socials (external links; everything inside the app itself is simulated). */
export const PROJECT = {
  ca: 'BnUj79vgt6Vv5WS85inxjbZ6Hy56bLUW8AB6uPSNxiom',
  x: 'https://x.com/PAPERXIOMTrade',
  pump: 'https://pump.fun/coin/BnUj79vgt6Vv5WS85inxjbZ6Hy56bLUW8AB6uPSNxiom',
  dex: 'https://dexscreener.com/solana/BnUj79vgt6Vv5WS85inxjbZ6Hy56bLUW8AB6uPSNxiom',
} as const;

export const PROTOCOL_VERSION = 1;
export const APP_VERSION = '1.0.0';

/** Simulation parameters. "traced" = taken from the reference client (market.js / game.js); "chosen" = our choice. */
export const SIM = {
  SOL_USD: 150, // traced: TX.SOL_USD = 150
  LAMPORTS: 1_000_000_000,
  TOKEN_DECIMALS: 6,
  TOTAL_SUPPLY_RAW: 1_000_000_000 * 1_000_000, // 1B tokens, 6 decimals
  V_SOL0: 30, // traced: virtual SOL starts at 30
  V_TOK0: 1_073_000_000, // traced: virtual tokens 1.073B
  TOKENS_ON_CURVE: 793_100_000, // pump.fun-style: 793.1M sellable on the curve; migration MC is derived in curve.ts (~$61.6K)
  AMM_SEED_SOL: 79, // post-migration pool seed (chosen, pump-style)
  AMM_SEED_TOKENS: 206_900_000,
  AMM_FEE_BPS: 25, // 0.25% pool fee after migration
  MIGRATION_FREEZE_MS: 2500, // traced: MIG_MS = 2500
  FINAL_STRETCH_IN: 35, // traced: FS_IN = 35 (% bonded)
  FINAL_STRETCH_OUT: 20, // traced: FS_OUT
  COLUMN_CAP: { new: 30, final: 6, migrated: 12 }, // traced: M.CAP
  TRADE_FEE_BPS: 100, // chosen: 1% curve fee
  DEPLOY_FEE_SOL: 0.03, // traced: DEPLOY_FEE
  TICK_MS: 250, // traced: 250 ms race grid
  START_BALANCE_SOL: 100, // realism pass v2: 100 SOL so the player cannot buy the whole curve several times
  QUICK_BUY_SOL: 0.5, // realism pass v2 (latest spec wins over handoff v3's 0)
  MAX_TOKENS: 400, // chosen bound on live+graveyard tokens kept in memory
  CANDLE_CAP: 1800, // 1s candles kept per token (30 minutes)
  TRADE_CAP: 250, // trades kept per token
  GLOBAL_TRADE_CAP: 400,
  DEFAULT_LATENCY_MS: 120,
  MAX_DEV_DEPLOYS: 3, // traced: DEV_DEPLOYS
  NAME_MAX: 32,
  /** Background regime budget (chosen): autonomous order flow steers non-catalyst coins back below this multiple of
   *  their launch MC. Player fills are never reversed; only future bot flow adapts. */
  BG_MAX_MUL: 3.5,
  /** Spawn probability per 250ms tick for background coins (chosen). */
  BG_SPAWN_P: 0.22,
  TICKER_MAX: 13,
} as const;

export const PLATFORMS = ['pump', 'bonk', 'bags', 'moonshot', 'believe', 'jupstudio'] as const;
export type Platform = (typeof PLATFORMS)[number];
export const PLATFORM_LABEL: Record<Platform, string> = { pump: 'Pump', bonk: 'Bonk', bags: 'Bags', moonshot: 'Moonshot', believe: 'Believe', jupstudio: 'Jup Studio' };
export const PLATFORM_COLOR: Record<Platform, string> = { pump: '#4fd18b', bonk: '#ff9d3c', bags: '#35d07f', moonshot: '#b18cff', believe: '#3fd2f2', jupstudio: '#c7f284' };
export const PAIRS = ['SOL', 'SPACEX', 'TESLA'] as const;
export type Pair = (typeof PAIRS)[number];
export const FEE_MODES = ['none', 'dividends', 'feesplit'] as const;
export type FeeMode = (typeof FEE_MODES)[number];
export const FEE_LABEL: Record<FeeMode, string> = { none: 'None', dividends: 'Dividends', feesplit: 'Fee Split' };

export const MODES = ['dev', 'og', 'first'] as const;
export type Mode = (typeof MODES)[number];
export const MODE_LABEL: Record<Mode, string> = { dev: 'DEV', og: 'OG', first: 'FIRST TX' };
export const DIFFS = ['casual', 'pro', 'sweat'] as const;
export type Difficulty = (typeof DIFFS)[number];
export const DIFF_LABEL: Record<Difficulty, string> = { casual: 'Casual', pro: 'Pro', sweat: 'Sweat' };

/** Traced difficulty tables (game.js). */
export const DIFF = {
  botFast: { casual: [1600, 2800], pro: [900, 1700], sweat: [450, 900] },
  flood: { casual: { n: [4, 7], win: 8000 }, pro: { n: [10, 16], win: 5000 }, sweat: { n: [80, 110], win: 3600 } }, // Sweat: Director-scale flood (B §6)
  vampDelay: { casual: [7000, 15000], pro: [5000, 11000], sweat: [4000, 8000] },
  devLate: { casual: { p: 0.35, at: [9000, 15000] }, pro: { p: 0.3, at: [6500, 11500] }, sweat: { p: 0.25, at: [4500, 8500] } },
  vampBot: { casual: [3000, 6000], pro: [1500, 3500], sweat: [800, 2000] },
  sniper: { casual: { first: [900, 1700], n: [5, 9], mul: [160, 420] }, pro: { first: [380, 850], n: [8, 14], mul: [320, 950] }, sweat: { first: [140, 420], n: [12, 20], mul: [650, 2600] } },
  timeoutMs: { dev: 25000, og: 15000, first: 15000 }, // traced CFG.timeoutMs
} as const satisfies Record<string, unknown>;

/** Minimum plausible human reaction for scored results (chosen). Anything faster is rejected by the server. */
export const MIN_REACTION_MS = 80;
export const PROGRESS_MAX_BYTES = 512 * 1024;
export const ROOM = { MAX_PLAYERS: 16, CHAT_CAP: 100, NAME_MAX: 20, MSG_MAX_BYTES: 32 * 1024, RATE_PER_SEC: 25 } as const;
